import { useEffect, useMemo, useState } from "react";
import { Ban, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";
import Button from "@/components/Core/Button/Button";
import { Pool } from "@/models/Organisation";
import { useOrganisation, useUpdateOrganisation } from "@/networking/organisation.hooks";
import { slugifyKey } from "@/utils/string.utils";
import { SettingsLeaf } from "./SettingsLeafLayout";

/**
 * "Pooler" — manage the organisation's pools.
 *
 * Pool keys are referenced by consultants, customers, subsidiaries and
 * job-service batch scoping, so pools are never deleted or re-keyed: existing
 * pools can only be renamed, deactivated or reactivated. Only rows that have
 * not been saved yet can be removed. The default pool cannot be deactivated —
 * pick another default first.
 */

/** Draft row: `key` is empty for new rows until saved (generated from name). */
type DraftPool = Pool & { isNew?: boolean };

const PoolerPage = () => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();
	const { mutate: updateOrganisation, isLoading: isSaving } = useUpdateOrganisation();

	const [draft, setDraft] = useState<DraftPool[]>([]);

	useEffect(() => {
		setDraft(organisation?.pools ?? []);
	}, [organisation?.pools]);

	const addPool = () => setDraft([...draft, { key: "", name: "", isNew: true }]);

	const updateName = (index: number, name: string) =>
		setDraft(draft.map((p, i) => (i === index ? { ...p, name } : p)));

	const removeNewPool = (index: number) => setDraft(draft.filter((_, i) => i !== index));

	const setDefault = (index: number) =>
		setDraft(draft.map((p, i) => ({ ...p, isDefault: i === index || undefined })));

	const toggleDeactivated = (index: number) =>
		setDraft(
			draft.map((p, i) => (i === index ? { ...p, deactivated: !p.deactivated || undefined } : p))
		);

	// Keys are generated from the name on save and must stay unique within the
	// organisation; collisions get a numeric suffix.
	const buildPayload = (): Pool[] => {
		const usedKeys = new Set(draft.filter((p) => !p.isNew).map((p) => p.key));

		return draft
			.filter((p) => p.name.trim().length > 0)
			.map(({ isNew, ...pool }) => {
				const name = pool.name.trim();

				if (!isNew) return { ...pool, name };

				const base = slugifyKey(name) || "pool";
				let key = base;
				for (let n = 2; usedKeys.has(key); n++) key = `${base}-${n}`;
				usedKeys.add(key);

				return { ...pool, name, key };
			});
	};

	const hasUnsavedChanges =
		JSON.stringify(buildPayload()) !== JSON.stringify(organisation?.pools ?? []);

	const savePools = () => updateOrganisation({ pools: buildPayload() });

	const activeCount = useMemo(() => draft.filter((p) => !p.deactivated).length, [draft]);

	return (
		<SettingsLeaf
			title="Pooler"
			description="Pooler grupperar konsulter och kunder inom organisationen. En pool kan inaktiveras men inte tas bort — inaktiverade pooler kan inte väljas för nya konsulter eller kunder, men befintliga kopplingar och historik påverkas inte."
		>
			<div className="flex max-w-[560px] flex-col gap-2">
				{draft.map((pool, index) => (
					<div
						key={pool.isNew ? `new-${index}` : pool.key}
						className={`flex items-center gap-2 ${pool.deactivated ? "opacity-60" : ""}`}
					>
						<AppInputNew
							containerClassName="flex-1 min-w-0"
							value={pool.name}
							onChangeText={(text) => updateName(index, text)}
							placeholder="Namn på poolen"
						/>

						{!pool.isNew && (
							<span className="shrink-0 rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-500">
								{pool.key}
							</span>
						)}

						<button
							type="button"
							onClick={() => setDefault(index)}
							disabled={!!pool.deactivated}
							title={pool.isDefault ? "Standardpool" : "Gör till standardpool"}
							className={`shrink-0 rounded-sm px-2 py-1 text-xs font-medium transition-colors ${
								pool.isDefault
									? "bg-action/10 text-action"
									: "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
							} disabled:pointer-events-none disabled:opacity-40`}
						>
							Standard
						</button>

						{pool.isNew ? (
							<button
								type="button"
								onClick={() => removeNewPool(index)}
								aria-label={t("REMOVE")}
								title={t("REMOVE")}
								className="flex h-[2.3125rem] w-[2.3125rem] shrink-0 items-center justify-center rounded-sm border border-transparent text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
							>
								<Trash2 size={18} />
							</button>
						) : (
							<button
								type="button"
								onClick={() => toggleDeactivated(index)}
								disabled={!!pool.isDefault || (!pool.deactivated && activeCount <= 1)}
								aria-label={pool.deactivated ? "Aktivera pool" : "Inaktivera pool"}
								title={
									pool.isDefault
										? "Standardpoolen kan inte inaktiveras — välj en annan standardpool först"
										: pool.deactivated
										? "Aktivera pool"
										: "Inaktivera pool"
								}
								className={`flex h-[2.3125rem] w-[2.3125rem] shrink-0 items-center justify-center rounded-sm border border-transparent transition-colors disabled:pointer-events-none disabled:opacity-40 ${
									pool.deactivated
										? "text-gray-400 hover:border-green-200 hover:bg-green-50 hover:text-green-600"
										: "text-gray-400 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
								}`}
							>
								{pool.deactivated ? <RotateCcw size={18} /> : <Ban size={18} />}
							</button>
						)}
					</div>
				))}
			</div>

			<div className="max-w-[560px]">
				<button
					type="button"
					onClick={addPool}
					className="flex items-center gap-1.5 rounded-sm px-1 py-1 text-sm font-medium text-action transition-colors hover:bg-action/10"
				>
					<Plus size={16} />
					Lägg till pool
				</button>
			</div>

			<div>
				<Button
					onClick={savePools}
					disabled={!hasUnsavedChanges || isSaving}
					height="37px"
					fontSize="s"
				>
					{t("SAVE_CHANGES")}
				</Button>
			</div>
		</SettingsLeaf>
	);
};

export default PoolerPage;
